import type { CSSProperties } from "react";

import { cn } from "@/lib/utils";
import { CarrierLogo } from "@/components/common/CarrierLogo";
import type { ScheduleEvent } from "@/types/schedule";
import { EVENT_DOT_COLORS } from "./scheduleConstants";

interface ScheduleDayViewProps {
  /** Events for the selected day, pre-sorted chronologically. */
  events: ScheduleEvent[];
  selectedId?: string | null;
  onSelect?: (event: ScheduleEvent) => void;
}

/** Single-day agenda: vertical list of milestones (date-only; no time column). */
export function ScheduleDayView({
  events,
  selectedId,
  onSelect,
}: ScheduleDayViewProps) {
  return (
    <ul className="flex flex-col divide-y divide-border/40">
      {events.map((ev, index) => (
        <li
          key={ev.id}
          className="schedule-event-pop"
          style={{ "--schedule-pop-index": index } as CSSProperties}
        >
          <button
            type="button"
            onClick={() => onSelect?.(ev)}
            className={cn(
              "flex min-h-11 w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors",
              onSelect && "cursor-pointer hover:bg-muted/40",
              selectedId === ev.id && "bg-muted/60 ring-1 ring-primary/40",
            )}
          >
            <span
              className={cn(
                "size-2.5 shrink-0 rounded-full",
                EVENT_DOT_COLORS[ev.kind],
              )}
            />
            <CarrierLogo carrier={ev.carrier} logoOnly size={18} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                {ev.title}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {ev.lane}
              </span>
            </span>
            {ev.count && ev.count > 1 && (
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
                {ev.count} bookings
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
